import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { createStyles, Divider, Grid, makeStyles, Tooltip, Typography, MenuItem } from '@material-ui/core';
import { CallReceived, CallMade, CheckCircle, ThumbsUpDown, Error, Cancel, WatchLater } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import {
  OperacaoInteroperabilidadeResponse,
  GrupoAcesso,
  TipoInteroperabilidade,
  StatusInteroperabilidade,
  Perfil,
} from 'common/genericTypes';
import { operacaoStatusDescriptions, interoperabilidadeStatusDescriptions } from 'common/genericDescriptions';
import { LgSpace, MdSpace, SmSpace } from 'm2m.components';
import MoreOptionsMenu from 'components/moreOptionsMenu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { formatDate } from 'utils/date';
import { addCpfCnpjMask } from 'utils/mask';
import { formatCurrencyNumber, formatEightDecimalsCurrency, formatNumber } from 'utils/number';

const useStyles = makeStyles(() =>
  createStyles({
    cpfCnpj: {
      width: '150px',
    },
  })
);

const Interoperabilidade: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const genericClasses = useGenericStyles();
  const classes = useStyles();

  const { getOperacoesInteroperabilidade } = useSelector((state: RootState) => state.operacaoApi);
  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const [operacoes, setOperacoes] = useState<Array<OperacaoInteroperabilidadeResponse>>([]);

  const getTransferencias = useCallback(async () => {
    try {
      const result = await getOperacoesInteroperabilidade(dispatch, true);
      setOperacoes(result);
    } catch {}
  }, [dispatch, getOperacoesInteroperabilidade]);

  useEffect(() => {
    getTransferencias();
  }, [getTransferencias]);

  const getStatusIcon = (status: StatusInteroperabilidade | undefined) => {
    switch (status) {
      case StatusInteroperabilidade.Pendente:
        return <WatchLater fontSize="small" className={genericClasses.warningIcon} />;
      case StatusInteroperabilidade.Reprovado:
        return <Cancel fontSize="small" className={genericClasses.errorIcon} />;
      case StatusInteroperabilidade.Aprovado:
        return <CheckCircle fontSize="small" className={genericClasses.successIcon} />;
      default:
        return <Error fontSize="small" className={genericClasses.warningIcon} />;
    }
  };

  return (
    <>
      <MdSpace />
      <Grid container justifyContent="flex-start" spacing={8}>
        <Grid item style={{ display: 'inline-flex' }}>
          <CheckCircle fontSize="small" className={genericClasses.successIcon} />
          <Typography display="inline">{'Concluída'}</Typography>
        </Grid>
        <Grid item style={{ display: 'inline-flex' }}>
          <WatchLater fontSize="small" className={genericClasses.warningIcon} />
          <Typography display="inline">{'Aguardando Confirmação'}</Typography>
        </Grid>
        <Grid item style={{ display: 'inline-flex' }}>
          <Cancel fontSize="small" className={genericClasses.errorIcon} />
          <Typography display="inline">{'Recusada'}</Typography>
        </Grid>
      </Grid>
      <MdSpace />
      <Grid container spacing={2}>
        <Grid item md={1} />
        <Grid item md={1}>
          <Typography variant="body2" color="textSecondary">
            {'Data'}
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant="body2" color="textSecondary">
            {'Instrumento'}
          </Typography>
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" color="textSecondary">
            {'Custodiante'}
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant="body2" color="textSecondary" className={classes.cpfCnpj}>
            {'Investidor'}
          </Typography>
        </Grid>
        <Grid item md={1}>
          <Typography variant="body2" color="textSecondary">
            {'Quantidade'}
          </Typography>
        </Grid>
        <Grid item md={1}>
          <Typography variant="body2" color="textSecondary">
            {'PU'}
          </Typography>
        </Grid>
        <Grid item md={1} />
      </Grid>
      <SmSpace />
      <Divider variant="fullWidth" />
      {operacoes
        .sort((a, b) => new Date(b.dataDeCriacao).getTime() - new Date(a.dataDeCriacao).getTime())
        .map((operacao) => (
          <React.Fragment key={operacao.id}>
            <SmSpace />
            <Grid container alignItems="center" spacing={2}>
              <Grid item md={1} style={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip
                  title={
                    operacao.interoperabilidade.statusAprovacao
                      ? interoperabilidadeStatusDescriptions[operacao.interoperabilidade.statusAprovacao]
                      : operacaoStatusDescriptions[operacao.status]
                  }
                  aria-label={
                    operacao.interoperabilidade.statusAprovacao
                      ? interoperabilidadeStatusDescriptions[operacao.interoperabilidade.statusAprovacao]
                      : operacaoStatusDescriptions[operacao.status]
                  }
                >
                  {getStatusIcon(operacao.interoperabilidade.statusAprovacao)}
                </Tooltip>
              </Grid>
              <Grid item md={1} style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" style={{ marginRight: '5px' }}>
                  {formatDate(operacao.dataDeCriacao)}
                </Typography>
                {operacao.interoperabilidade.tipo === TipoInteroperabilidade.Entrada ? (
                  <Tooltip title={'Entrada'} aria-label={'Entrada'}>
                    <CallReceived fontSize="small" color={'primary'} />
                  </Tooltip>
                ) : (
                  <Tooltip title={'Saída'} aria-label={'Saída'}>
                    <CallMade fontSize="small" color={'error'} />
                  </Tooltip>
                )}
              </Grid>
              <Grid item md={2}>
                <Typography variant="body2">{operacao.instrumentoFinanceiro.codigo}</Typography>
              </Grid>
              <Grid item md={3} style={{ overflow: 'hidden' }}>
                <Tooltip title={operacao.custodiante.razaoSocial} aria-label={operacao.custodiante.razaoSocial}>
                  <Typography variant="body2" className={genericClasses.wrapTxt}>
                    {operacao.custodiante.razaoSocial}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item md={2}>
                <Typography variant="body2" className={classes.cpfCnpj}>
                  {addCpfCnpjMask(operacao.interoperabilidade?.investidor?.cpfCnpj)}
                </Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="body2">{formatNumber(operacao.interoperabilidade?.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}</Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="body2">
                  {formatEightDecimalsCurrency(operacao.interoperabilidade?.precoUnitario)}
                </Typography>
              </Grid>
              <Grid item md={1}>
                {isUsuarioOperacoes &&
                  !isUsuarioLeitura &&
                  operacao.interoperabilidade.statusAprovacao === StatusInteroperabilidade.Pendente && (
                    <MoreOptionsMenu>
                      <MenuItem
                        onClick={() => history.push('/operacoes/gerenciar/interoperabilidade/detalhes', operacao)}
                      >
                        <ThumbsUpDown fontSize="small" style={{ marginRight: '10px' }} />
                        {'Confirmar/Recusar'}
                      </MenuItem>
                    </MoreOptionsMenu>
                  )}
              </Grid>
            </Grid>
            <SmSpace />
            <Divider variant="fullWidth" />
          </React.Fragment>
        ))}
      <LgSpace />
    </>
  );
};

export default Interoperabilidade;
